import axios from 'axios';
import store from '@/store';
import i18n from '@/lang/index';
import { getConfig } from '@/setting';

const { hostHttpProtocol, host } = getConfig();

// Token 刷新狀態管理
let isRefreshing = false;
let refreshQueue = [];

// 創建一個用於獲取 refreshToken 的 Promise
let refreshTokenPromise = null;

// 定義提前刷新的時間（單位：毫秒）
// 在 token 還有 30 分鐘過期時就開始刷新
const REFRESH_THRESHOLD = 30 * 60 * 1000;

/**
 * 處理 Token 刷新的服務類
 */
class TokenRefreshService {
  /**
   * 嘗試刷新 Token
   * @param {Object} originalRequest 原始請求配置
   * @param {Function} axiosProxyInstance axios代理實例，用於重試請求
   * @returns {Promise} 處理結果
   */
  async handleTokenRefresh(originalRequest, axiosProxyInstance) {
    // 立即標記請求已經嘗試過刷新，避免循環
    originalRequest._retry = true;

    // 如果已經有刷新流程在進行中，將當前請求添加到隊列
    if (isRefreshing) {
      console.log('Token 刷新已在進行中，將請求添加到隊列');
      return this.enqueueRequest(originalRequest, axiosProxyInstance);
    }

    // 設置刷新狀態為 true，防止其他請求同時進入刷新流程
    isRefreshing = true;

    try {
      // 使用單一 Promise 來處理 Token 刷新
      if (!refreshTokenPromise) {
        console.log('開始刷新 Token');
        refreshTokenPromise = this.attemptTokenRefresh();
      } else {
        console.log('使用已存在的 Token 刷新 Promise');
      }

      // 等待 Token 刷新完成
      const returnRes = await refreshTokenPromise;

      // 使用新 Token 重試原始請求
      const result = await this.retryOriginalRequest(originalRequest, returnRes);

      // 處理隊列中等待的請求
      await this.processQueue(this.getNewestToken(returnRes));

      // 更新 token 過期時間
      this.updateTokenExpiration(returnRes);

      return result;
    } catch (err) {
      console.log('handleTokenRefresh Error', err);
      // 處理失敗，清理狀態
      this.resetState();
      store.commit("SET_IS_LOGIN", false);

      // 顯示錯誤消息
      MESSAGE("e", i18n.t('warn.tokenExpired'));

      return Promise.reject(err?.response?.data || {
        message: '請求失敗，請稍後再試'
      });
    } finally {
      // 清理資源
      isRefreshing = false;
      refreshTokenPromise = null;
    }
  }

  /**
   * 多次嘗試刷新 Token
   * @returns {Promise} 刷新結果
   */
  async attemptTokenRefresh() {
    try {
      // 第一次嘗試
      return await store.dispatch("GetRefreshToken", true);
    } catch (err) {
      try {
        // 第二次嘗試
        return await store.dispatch("GetRefreshToken", true);
      } catch (err2) {
        // 第三次嘗試 - 從 App 內存獲取
        try {
          // 從 App 內存獲取 token
          appCallReadStorage('userInfo');

          // 等待 App 回傳 token（通過 WebInterface.returnUserInfo）
          await new Promise(resolve => setTimeout(resolve, 1000));  // 給予足夠時間讓 App 回傳

          // 使用更新後的 token 進行第三次嘗試
          return await store.dispatch("GetRefreshToken", true);
        } catch (err3) {
          throw err3;
        }
      }
    }
  }

  /**
   * 使用新 Token 重試原始請求
   * @param {Object} originalRequest 原始請求
   * @param {Object} returnRes Token 刷新返回的結果
   * @returns {Promise} 請求結果
   */
  async retryOriginalRequest(originalRequest, returnRes) {
    const { url, method, params, data, headers } = originalRequest;

    const newToken = this.getNewestToken(returnRes);

    // 正確處理請求數據，支持 FormData
    let processedData;
    if (data) {
      processedData = data instanceof FormData
        ? data
        : (typeof data === 'string' ? JSON.parse(data) : data);
    }

    // 使用原始的 axios 實例，避免進入攔截器循環
    const newRes = await axios({
      baseURL: `${hostHttpProtocol}://${host}/api/v1`,
      url,
      method,
      timeout: API_TIMEOUT,
      params: params || undefined,
      data: processedData || undefined,
      headers: {
        ...headers,
        Authorization: `Bearer ${newToken}`
      }
    });

    return Promise.resolve(newToken);
  }

  /**
   * 將請求添加到隊列
   * @param {Object} originalRequest 原始請求
   * @param {Function} axiosProxyInstance axios代理實例
   * @returns {Promise} 請求結果 Promise
   */
  enqueueRequest(originalRequest, axiosProxyInstance) {
    return new Promise((resolve) => {
      refreshQueue.push(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        // 使用傳入的 axiosProxy 實例而不是直接使用 axios
        resolve(axiosProxyInstance(originalRequest));
      });
    });
  }

  /**
   * 處理隊列中的請求
   * @param {string} token 新的 Token
   * @returns {Promise} 所有隊列請求的完成狀態
   */
  async processQueue(token) {
    const promises = refreshQueue.map(cb => cb(token));
    refreshQueue = [];
    return Promise.all(promises);
  }

  /**
   * 重置刷新狀態
   */
  resetState() {
    isRefreshing = false;
    refreshTokenPromise = null;
    refreshQueue = [];
  }

  /**
   * 獲取最新的 token
   */
  getNewestToken(returnRes) {
    const newToken = returnRes?.data?.idToken || store.getters?.cache?.token || undefined;
    return newToken;
  }

  /**
   * 檢查是否需要刷新 Token
   * @param {Object} error 錯誤對象
   * @param {Object} originalRequest 原始請求
   * @returns {boolean} 是否需要刷新
   */
  shouldRefreshToken(error, originalRequest) {
    return (
      (error.response?.status === 401 ||
        (originalRequest?.url === '/app/auth/token/refresh' &&
          error.response?.data?.message?.includes('找不到此 refresh token'))
      ) && !originalRequest._retry
    );
  }

  /**
   * 檢查是否正在刷新 Token
   * @returns {boolean} 是否正在刷新
   */
  isRefreshing() {
    return isRefreshing;
  }

  /**
   * 更新 Token 過期時間
   * @param {Object} tokenResponse Token 刷新返回的結果
   */
  updateTokenExpiration(tokenResponse) {
    if (tokenResponse?.data?.expiresIn) {
      const expiresIn = tokenResponse.data.expiresIn;
      const expirationTime = Date.now() + expiresIn * 1000; // 轉換為毫秒

      // 將過期時間存儲到 store 中
      store.commit("cache/SET_TOKEN_EXPIRATION_TIME", expirationTime);

      // 將過期時間保存到 localStorage 中，以便在應用重啟後仍然可用
      appCallSaveStorage({
        key: 'tokenExpirationTime',
        value: expirationTime
      });

      // 設置自動刷新計時器
      this.scheduleTokenRefresh(expirationTime);
    }
  }

  /**
   * 檢查 Token 是否即將過期
   * @returns {boolean} 是否即將過期
   */
  isTokenNearExpiration() {
    const expirationTime = store.getters.tokenExpirationTime;

    if (!expirationTime) {
      return false;
    }

    return Date.now() + REFRESH_THRESHOLD > expirationTime;
  }

  /**
   * 設置自動刷新計時器
   * @param {number} expirationTime Token 過期時間戳
   */
  scheduleTokenRefresh(expirationTime) {
    // 清除可能存在的舊計時器
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // 計算何時應該刷新 Token
    const timeUntilRefresh = expirationTime - Date.now() - REFRESH_THRESHOLD;

    // 只有當時間合理時才設置計時器
    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(() => {
        this.proactiveTokenRefresh();
      }, timeUntilRefresh);
    } else {
      // 如果已經過了應該刷新的時間，立即刷新
      this.proactiveTokenRefresh();
    }
  }

  /**
   * 主動刷新 Token
   */
  async proactiveTokenRefresh() {
    // 確保使用者已登入且 token 仍然有效
    if (!store.state.isLogin) {
      return;
    }

    try {
      if (!isRefreshing) {
        isRefreshing = true;
        const result = await this.attemptTokenRefresh();
        this.updateTokenExpiration(result);
        isRefreshing = false;
      }
    } catch (error) {
      isRefreshing = false;
      console.error('主動刷新 Token 失敗:', error);

      // 如果自動刷新失敗，可以選擇重試或顯示通知等
      if (error.response?.status === 401) {
        // Token 已經完全失效，需要用戶重新登入
        store.commit("SET_IS_LOGIN", false);
        MESSAGE("e", i18n.t('warn.tokenExpired'));
      }
    }
  }

  /**
   * 初始化 Token 刷新機制
   * 在應用啟動時調用
   */
  initTokenRefresh() {
    // 嘗試從 store 獲取過期時間
    let expirationTime = store.getters.tokenExpirationTime;

    // 如果 store 中沒有，嘗試從 localStorage 獲取
    if (!expirationTime) {
      appCallReadStorage('tokenExpirationTime');

      // 給予足夠時間讓 App 回傳
      setTimeout(() => {
        expirationTime = store.getters.tokenExpirationTime;

        if (expirationTime) {
          // 如果 Token 即將過期，立即刷新
          if (this.isTokenNearExpiration()) {
            this.proactiveTokenRefresh();
          } else {
            // 否則設置計時器
            this.scheduleTokenRefresh(expirationTime);
          }
        }
      }, 1000);
    } else {
      // 如果 Token 即將過期，立即刷新
      if (this.isTokenNearExpiration()) {
        this.proactiveTokenRefresh();
      } else {
        // 否則設置計時器
        this.scheduleTokenRefresh(expirationTime);
      }
    }
  }
}

export default new TokenRefreshService(); 